import * as React from "react";
import {
  Checkbox as ChakraCheckBox,
  CheckboxProps as ChakraCheckboxProps,
} from "@chakra-ui/checkbox";
import { Stack, Text, Box, BoxProps } from "@chakra-ui/react";
import { FieldError } from "react-hook-form";

import { TranslationKeys } from "utils/localization";
import { useLocalization } from "hooks/useLocalization";
import { resolveChakraSpacing } from "./helpers";

type CheckboxProps = ChakraCheckboxProps & {
  label?: React.ReactNode;
  optionLabel?: React.ReactNode;
  error?: FieldError;
  customError?: TranslationKeys;
  customMessage?: React.ReactNode;
  isDisabled?: boolean;
  isChecked?: boolean;
  textVariant?: "small_1" | "small_2" | "medium_1";
};

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      optionLabel,
      error,
      customMessage,
      customError,
      isDisabled,
      isChecked,
      textVariant = "small_1",
      ...props
    },
    ref,
  ) => {
    const newProps = resolveChakraSpacing<CheckboxProps, CheckboxProps>(props);
    const { translate } = useLocalization();

    return (
      <Box {...(newProps.spacing as BoxProps)}>
        <ChakraCheckBox
          ref={ref}
          isDisabled={isDisabled}
          isChecked={isChecked}
          colorScheme="checkBoxColor"
          borderColor="ud-grey-200"
          _hover={{ borderColor: "ud-primary" }}
          transition="all 0.3s"
          {...props}
        >
          <Stack direction="row" align="center">
            {label && React.isValidElement(label) ? (
              label
            ) : (
              <Text variant={textVariant}>{label}</Text>
            )}
            {optionLabel && React.isValidElement(optionLabel) ? (
              optionLabel
            ) : (
              <Text variant={textVariant} color="ud-grey-400">
                {optionLabel}
              </Text>
            )}
          </Stack>
        </ChakraCheckBox>
        {error?.message && (
          <Text variant="form-error">
            {customError ? translate(customError) : error.message}
          </Text>
        )}
        {customMessage && <Text variant="form-message">{customMessage}</Text>}
      </Box>
    );
  },
);

export default Checkbox;
